import { useMemo } from 'react';
import { TFunction } from 'i18next';
import { useLang } from 'hooks/lang';

import { actions, TaskComment, TaskFile } from 'entities';

import { Comments } from 'components/Comments';

import { useTaskModal } from '../../context.taskModal';
import { sortByProperty } from 'utils/array';

import { NormalizeCommentFilesModal, useEnsureCommentFileState } from './NormalizeCommentFiles';

interface TasksCommentsProps {
  canEdit: boolean;
}

function filesToComments(files: TaskFile[], t: TFunction): TaskComment[] {
  return files
    .map(({ uploadedBy, name, url, uploadedAt, ...data }) => ({
      // blocks it inside our Comments comp
      noUpdate: true,

      user: uploadedBy,
      text: t('uploaded-a-file'),
      timestamp: uploadedAt,
      ...data,
      files: [
        {
          name,
          url,
          id: url,
          filename: data.filename,
          version: data.version,
          type:
            data.contentType ||
            (['.png', '.jpeg', '.jpg', '.webp'].some((imgRef) => url.includes(imgRef))
              ? 'image/png'
              : 'xx'),
        },
      ],
    }))
    .filter((f) => !f.commentId);
}

export function TasksComments({ canEdit }: TasksCommentsProps) {
  const { t } = useLang();

  const { task, updateTask } = useTaskModal();

  const { commentKey, initFileValidation, finishFileValidation, ...fileModalProps } =
    useEnsureCommentFileState();

  const create = actions.projects.createTaskComment.use({
    onSuccess: (comment) => {
      updateTask((old) => ({
        ...old,

        comments: [comment, ...(old.comments || [])],
      }));

      finishFileValidation();
    },
  });

  const createComment = async (newComment: string, files?: File[]) => {
    if (!newComment) return;

    if (files?.length) return initFileValidation(newComment, files);

    const comment = await create.execute({
      task: task.id,
      project: task.projectId,
      text: newComment,
    });

    return !!comment;
  };

  const update = actions.projects.updateTaskComment.use({
    onSuccess: (editedComment) => {
      updateTask((old) => ({
        ...old,

        comments: old.comments.map((comment) =>
          comment.id === editedComment.id ? { ...editedComment, edited: true } : comment,
        ),
      }));
    },
  });

  const updateComment = async (text: string, editedComment: TaskComment) => {
    await update.execute({
      task: editedComment.task,
      timestamp: editedComment.timestamp,
      text,
    });
  };

  const actualComments = useMemo(() => {
    return sortByProperty(
      [...(task.comments || []), ...filesToComments(task.files || [], t)],
      'timestamp',
      { timestamp: 'DESC' },
    );
  }, [task.files, task.comments, t]);

  return (
    <>
      <Comments
        key={commentKey}
        addTitle={false}
        canEdit={canEdit}
        comments={actualComments}
        onCreate={createComment}
        onEdit={updateComment}
        className="border-0 p-0"
        historyTarget="projects.taskCommentHistory"
      />

      <NormalizeCommentFilesModal {...fileModalProps} createComment={create} />
    </>
  );
}
